import { useCallback, useMemo, type ReactElement } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  KpiManager,
  type KpiCreateOutcome,
  type KpiEditCloseOutcome,
  type KpiManagerSection,
} from "@playbookai/kpi-feature";
import { hasEarlierInAppEntry, PeopleSourceProvider } from "@playbookai/ui";
import { createKpiOwnerDirectory } from "../../api/kpi-owner-directory.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { useTenantPeopleSource } from "../../hooks/useTenantPeopleSource.js";
import { canGovernKpis } from "../../auth/roles.js";
import { RequireTenantAdmin } from "../../auth/RequireTenantAdmin.js";
import {
  SETTINGS_KPI_ARCHIVED_ROUTE,
  SETTINGS_KPI_CREATE_ROUTE,
  SETTINGS_KPI_LIBRARY_ROUTE,
  SETTINGS_KPI_ROUTE,
  settingsKpiDetailRoute,
  settingsKpiEditRoute,
} from "../../routes.js";
import { useKpiSettingsRoute } from "./use-kpi-settings-route.js";

/** `?definitionId` — the KPI Type the Library's "Use template" carries over. */
const DEFINITION_PARAM = "definitionId";

/**
 * Settings host for the KPI Manager (PB-94). The shell is the composition root
 * (ADR-0009): it injects an owner directory built from the tenant
 * `admin-users-api`, so `@playbookai/kpi-feature` stays free of the auth-admin
 * coupling — and it owns every React Router concern, handing the feature only
 * route-derived state and navigation callbacks (the feature package has no
 * `react-router-dom` dependency).
 *
 * This host is mounted for EVERY KPI URL — `/settings/kpi`,
 * `/settings/kpi/create`, `/settings/kpi/:kpiId` and `/settings/kpi/:kpiId/edit`
 * (PB-2941) — because all of them are children of the same route element. So it
 * stays mounted across them: browse state survives a trip to any child, an
 * unsaved draft survives long enough for the feature to ask before discarding
 * it, and Back from a detail page restores the collection's search, filters,
 * layout and loaded pages rather than rebuilding them.
 *
 * ACCESS IS PER-URL, NOT PER-ROUTE, AND THAT IS THE POINT (PB-2941).
 * `/settings/kpi`, `/settings/kpi/create` and `/settings/kpi/:kpiId/edit` are
 * Tenant-Admin, and a non-admin gets the shared `RequireTenantAdmin` denial.
 * Only `/settings/kpi/:kpiId` is open to any authenticated tenant member,
 * because a KPI's OWNER — routinely NOT an admin — has to reach it to record a
 * value, and the settings overview never offers them the tile, so a direct link
 * is the only way in. A route-level gate in `App.tsx` could not express that
 * split: it would have to decide before this component knows which URL it is on,
 * let alone who owns the KPI.
 *
 * `canManage` is therefore DERIVED (admin) rather than hardcoded, and it gates
 * only the governance affordances — edit metadata, assign owner, set target,
 * activate, archive. It deliberately does NOT gate `Add value` or `Add
 * historical values`, which the feature shows to the KPI's owner alone; being an
 * admin does not grant an override (ADR-0052).
 *
 * UI shaping only. playbook-be asserts the `tenant-admin` realm role on every
 * governance mutation — including `PATCH /v1/kpis/:id` — and the OWNER on a
 * value write, so a member who reaches a KPI they do not own sees a read-only
 * page and gets 403s if they force past it. The KPI READ itself is member-open
 * by contract (PB-93), so rendering the detail page for a non-owner leaks
 * nothing they could not already fetch.
 */
export function KpiSettings(): ReactElement {
  const ownerDirectory = useMemo(() => createKpiOwnerDirectory(), []);
  // The KPI owner fields' PeopleSource: the tenant directory, grant mode
  // `none` — a KPI has no access list, so an owner gains no access.
  const peopleSource = useTenantPeopleSource();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const claims = useAuthStore((s) => s.claims);
  // KPI governance, not workspace administration — the two came apart when
  // `assertMayGovernKpis` started accepting a Transformation Lead (PB-5189).
  // Gating on workspace-admin left that role holding an API permission it
  // could not reach on any screen.
  const canManage = canGovernKpis(claims);

  // Shared with `SettingsPage`'s breadcrumb, so the two cannot disagree about
  // which URL is which — see `useKpiSettingsRoute` for why this is `useMatch`
  // rather than `useParams` or a pathname split.
  const {
    createMode,
    editMode,
    kpiId: detailKpiId,
    section,
  } = useKpiSettingsRoute();

  // The Settings rail lists the three collections as child items (PB-5134,
  // d3), so the section is a URL and the feature's own rail stays hidden.
  const handleSectionChange = useCallback(
    (next: KpiManagerSection) => {
      navigate(
        next === "library"
          ? SETTINGS_KPI_LIBRARY_ROUTE
          : next === "archived"
            ? SETTINGS_KPI_ARCHIVED_ROUTE
            : SETTINGS_KPI_ROUTE,
      );
    },
    [navigate],
  );

  const handleOpenCreate = useCallback(
    (definitionId: string | null) => {
      // MERGED into the current search, never rebuilt from scratch: the
      // collection's applied filters live in the URL (`?owner=…`), and a
      // fresh `URLSearchParams` dropped them on the way into Create — so the
      // replace-based ways back landed on an unfiltered collection. This
      // callback owns exactly ONE key; everything else is carried through.
      const params = new URLSearchParams(searchParams);
      if (definitionId) {
        params.set(DEFINITION_PARAM, definitionId);
      } else {
        // A template-less create must not inherit a stale `?definitionId`
        // from a previous template flow still sitting in the URL.
        params.delete(DEFINITION_PARAM);
      }
      const search = params.toString();
      // Stamped like `handleOpenKpi`, for the form's "All KPIs" link.
      navigate(`${SETTINGS_KPI_CREATE_ROUTE}${search ? `?${search}` : ""}`, {
        state: { fromCollection: `${location.pathname}${location.search}` },
      });
    },
    [navigate, searchParams, location.pathname, location.search],
  );

  const handleCloseCreate = useCallback(
    (outcome: KpiCreateOutcome) => {
      // The form's "All KPIs" link: pop only when All KPIs IS what is behind
      // the form (restoring its filters); from the Library or Archived, land
      // on All KPIs in place of the form instead of on the list the label did
      // not name. The plain Cancel below still goes back wherever it came from.
      if (outcome === "all-kpis") {
        const origin = (location.state as { fromCollection?: unknown } | null)
          ?.fromCollection;
        if (
          typeof origin === "string" &&
          isAllKpisUrl(origin) &&
          location.key !== "default" &&
          hasEarlierInAppEntry()
        ) {
          navigate(-1);
          return;
        }
        navigate(SETTINGS_KPI_ROUTE, { replace: true });
        return;
      }
      // Cancel goes back to whichever KPI screen preceded the create page. A
      // create URL opened directly has no such entry — React Router keys that
      // first location "default" — so it falls back to the KPI Manager. So
      // does one reached by a REPLACE onto the first entry (the sign-in
      // callback): its key is not "default", but a pop would leave the app.
      // A successful create always lands on All KPIs, replacing the create
      // entry so Back never returns to the submitted form.
      if (
        outcome === "cancelled" &&
        location.key !== "default" &&
        hasEarlierInAppEntry()
      ) {
        navigate(-1);
        return;
      }
      navigate(SETTINGS_KPI_ROUTE, { replace: true });
    },
    [location.key, location.state, navigate],
  );

  const handleOpenKpi = useCallback(
    (kpiId: string) => {
      // Stamp WHERE the reader came from. Closing the KPI goes back one entry,
      // which restores the collection's filters and scroll — but only when the
      // previous entry really IS the collection. Since PB-5317 it often is not:
      // a reader can arrive from the shared TTI page, and popping then took them
      // straight back to it under a button labelled "All KPIs".
      //
      // Opened straight after a create, `onCloseCreate("created")` has ALREADY
      // replaced the form with All KPIs, but this closure still sees the
      // create URL — so stamp what is now actually behind the KPI.
      const here = isCreateUrl(location.pathname)
        ? SETTINGS_KPI_ROUTE
        : `${location.pathname}${location.search}`;
      navigate(settingsKpiDetailRoute(kpiId), {
        state: { fromCollection: here },
      });
    },
    [navigate, location.pathname, location.search],
  );

  const handleOpenTti = useCallback(
    (ttiId: string) => {
      // The shared Target-to-Improve page (PB-5317) — the same URL the Hoshin
      // bowling chart opens, addressed by the TTI id alone. `from` is how that
      // page's Back returns here: it is given no plan id, and this surface has
      // none to give.
      navigate(`/app/tti/${ttiId}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search],
  );

  const handleOpenEdit = useCallback(
    (kpiId: string) => {
      // Edit is another presentation of the selected KPI, not a second item in
      // the browse history. Replacing the detail entry prevents Close/Save from
      // leaving two consecutive copies of the same KPI behind; after the edit
      // route is replaced with the detail route, Back correctly returns to the
      // collection instead of appearing to do nothing on a duplicate URL.
      // `state` is carried: `replace` drops router state, and losing the
      // origin stamp here would quietly degrade this KPI's Back for the rest of
      // the visit — the collection's filters would stop being restored, with
      // nothing on screen to say why.
      //
      // From a COLLECTION row's menu there is no detail entry to replace:
      // replacing then overwrote the collection itself, losing its filters and
      // taking it out of Back. Push instead, stamped like `handleOpenKpi`, so
      // Save lands on the KPI and its "All KPIs" pops to the filtered list.
      // `editFrom` marks this edit entry as sitting directly on that list,
      // which is what lets a clean Cancel pop straight back to it
      // (`handleCloseEdit`).
      if (!detailKpiId) {
        navigate(settingsKpiEditRoute(kpiId), {
          state: {
            fromCollection: `${location.pathname}${location.search}`,
            editFrom: "collection",
          },
        });
        return;
      }
      navigate(settingsKpiEditRoute(kpiId), {
        replace: true,
        state: location.state,
      });
    },
    [detailKpiId, navigate, location.pathname, location.search, location.state],
  );

  const handleCloseEdit = useCallback(
    (outcome: KpiEditCloseOutcome) => {
      const state = location.state as {
        fromCollection?: unknown;
        editFrom?: unknown;
      } | null;
      // A clean Cancel of an edit opened from the collection returns to it: the
      // previous entry IS the filtered list, so going back restores its filters
      // and scroll. `"left"` (a dirty Cancel, the section rail) and `"saved"`
      // fall through to the KPI's page. A deep link has no previous entry —
      // React Router keys a first location "default".
      if (
        outcome === "cancelled" &&
        state?.editFrom === "collection" &&
        location.key !== "default"
      ) {
        navigate(-1);
        return;
      }
      // Back to the KPI, not back in history: leaving the edit form should
      // always land on the thing being edited, and going back could return to
      // whatever preceded it (the collection, a search, another KPI). `replace`
      // keeps the edit URL out of the forward path so Back from the KPI does
      // not re-open a form that was just cancelled or saved.
      if (detailKpiId) {
        // Same reason as `handleOpenEdit`: carry the origin stamp across the
        // replace, or the KPI this returns to forgets where the reader came
        // from. `editFrom` is dropped on purpose: it describes the edit entry
        // only, and the KPI's own Edit button must not inherit it.
        const origin = state?.fromCollection;
        navigate(settingsKpiDetailRoute(detailKpiId), {
          replace: true,
          state: typeof origin === "string" ? { fromCollection: origin } : null,
        });
        return;
      }
      navigate(SETTINGS_KPI_ROUTE, { replace: true });
    },
    [detailKpiId, location.key, location.state, navigate],
  );

  const handleCloseKpi = useCallback(() => {
    // Prefer real history — going BACK restores the collection's URL (and so its
    // applied filters) and its scroll position — but ONLY when the previous
    // entry is the collection, which the stamp above is how we know.
    //
    // Without that test this button went back one entry whatever it was, and
    // the label lied: a reader who reached the KPI from the shared TTI page was
    // sent back to the TTI page, whose own Back returned here, with no way to
    // reach the collection at all. A deep link has no prior entry either —
    // React Router keys a first location "default".
    const origin = (location.state as { fromCollection?: unknown } | null)
      ?.fromCollection;
    // The button says "All KPIs", so only All KPIs (with whatever filters it
    // had) counts: a KPI opened from Archived or the Library goes to All KPIs
    // rather than back to a list the label did not name.
    const cameFromCollection =
      typeof origin === "string" && isAllKpisUrl(origin) ? origin : null;
    // And only with an in-app entry behind it to pop to — the same test the
    // create form's Cancel and the TTI page use (`hasEarlierInAppEntry`).
    if (
      cameFromCollection !== null &&
      location.key !== "default" &&
      hasEarlierInAppEntry()
    ) {
      navigate(-1);
      return;
    }
    navigate(cameFromCollection ?? SETTINGS_KPI_ROUTE);
  }, [location.key, location.state, navigate]);

  // The admin-only URLs are Create and a KPI's EDIT form. Browse opens to
  // every member READ-ONLY (PB-5134, d4): the collection is member-open by
  // contract and a KPI Owner is routinely not an admin; `canManage=false`
  // hides Create and the governance row actions, and playbook-be asserts the
  // realm role on every governance mutation regardless.
  //
  // Denying here rather than letting the feature render read-only: a non-admin
  // on `/create` or `/edit` would otherwise get a page under a URL that says
  // the form exists and is simply not showing.
  //
  // Branching only for a NON-admin is what keeps the admin's element tree
  // identical across every KPI URL — swapping the gate in and out at the same
  // position would remount the manager and discard the browse state this host
  // exists to preserve.
  if (!canManage && (createMode || editMode)) {
    return (
      <RequireTenantAdmin
        area="KPI governance"
        title="KPI management access required"
        description="Transformation Leads manage KPIs. Contact a Transformation Lead for help."
      >
        {null}
      </RequireTenantAdmin>
    );
  }

  return (
    <PeopleSourceProvider source={peopleSource}>
      <KpiManager
        ownerDirectory={ownerDirectory}
        canManage={canManage}
        createMode={createMode}
        createDefinitionId={searchParams.get(DEFINITION_PARAM)}
        onOpenCreate={handleOpenCreate}
        onCloseCreate={handleCloseCreate}
        detailKpiId={detailKpiId}
        onOpenKpi={handleOpenKpi}
        onCloseKpi={handleCloseKpi}
        editMode={editMode}
        onOpenEdit={handleOpenEdit}
        onNavigateUrl={navigate}
        onOpenTti={handleOpenTti}
        onCloseEdit={handleCloseEdit}
        section={section}
        onSectionChange={handleSectionChange}
      />
    </PeopleSourceProvider>
  );
}

/** A URL's path, with trailing slashes dropped as `useKpiSettingsRoute` does. */
function pathOf(url: string): string {
  return (url.split(/[?#]/)[0] ?? "").replace(/\/+$/, "");
}

/** All KPIs, with or without its filters. */
function isAllKpisUrl(url: string): boolean {
  return pathOf(url) === SETTINGS_KPI_ROUTE;
}

function isCreateUrl(url: string): boolean {
  return pathOf(url) === SETTINGS_KPI_CREATE_ROUTE;
}
